import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Eye, EyeOff, Clock, AlertCircle, Maximize2 } from 'lucide-react';
import toast from 'react-hot-toast';
import Logo from '../components/Logo';
import SuspendedUserPopup from '../components/SuspendedUserPopup';
import { toggleFullscreen } from '../utils/fullscreenUtils';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState<string | null>(null);
  const [showPendingPopup, setShowPendingPopup] = useState(false);
  const [showRejectedPopup, setShowRejectedPopup] = useState(false);
  const [showSuspendedPopup, setShowSuspendedPopup] = useState(false);
  const [suspendedUserReason, setSuspendedUserReason] = useState<string>('');

  const { login, signInWithGoogle, signInWithFacebook, signInWithTwitter, signInWithInstagram } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsLoading(true);

    try {
      const userData = await login(email, password);
      
      // Check if user is approved
      if (userData.status === 'pending') {
        setShowPendingPopup(true);
        // Don't proceed with login - user should wait for approval
        return;
      }
      
      if (userData.status === 'rejected') {
        setShowRejectedPopup(true);
        // Don't proceed with login - user is rejected
        return;
      }
      
      if (userData.status === 'suspended') {
        setShowSuspendedPopup(true);
        // Don't proceed with login - user is suspended
        return;
      }
      
      // Only proceed if user is approved
      if (userData.status === 'approved') {
        toast.success('Login successful!');
        
        // Enter fullscreen mode after successful login
        try {
          await toggleFullscreen();
          // toast.success('Welcome to Live Trading Studio! Fullscreen mode activated.');
        } catch (fullscreenError) {
          // Continue with navigation even if fullscreen fails
        }
        
        navigate('/');
      } else {
        toast.error('Account status unknown. Please contact support.');
      }
    } catch (error: any) {
      // Handle specific error cases
      if (error.message === 'ACCOUNT_SUSPENDED') {
        setSuspendedUserReason('Account suspended due to KYC not fulfilled, unauthorized use, or too many attempts.');
        setShowSuspendedPopup(true);
        return;
      }
      
      toast.error(error.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialSignIn = async (provider: string, signInMethod: () => Promise<any>) => {
    setSocialLoading(provider);
    try {
      const userData = await signInMethod();
      
      // Check if user is approved
      if (userData.status === 'pending') {
        setShowPendingPopup(true);
        return;
      }
      
      if (userData.status === 'rejected') {
        setShowRejectedPopup(true);
        return;
      }
      
      if (userData.status === 'suspended') {
        setShowSuspendedPopup(true);
        return;
      }
      
      // Only proceed if user is approved
      if (userData.status === 'approved') {
        toast.success(`Successfully signed in with ${provider}!`);
        
        // Enter fullscreen mode after successful login
        try {
          await toggleFullscreen();
          // toast.success('Welcome to Live Trading Studio! Fullscreen mode activated.');
        } catch (fullscreenError) {
          // Continue with navigation even if fullscreen fails
        }
        
        navigate('/');
      } else {
        toast.error('Account status unknown. Please contact support.');
      }
    } catch (error: any) {
      toast.error(error.message || `Failed to sign in with ${provider}`);
    } finally {
      setSocialLoading(null);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card">
          <div className="text-center">
            <div className="flex justify-center mb-3">
              <Logo size="xxxl" />
            </div>
            <h2 className="auth-title">
              Let's respect the market
            </h2>
          </div>
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium theme-text-secondary"
              >
                {/* Email address */}
              </label>
              <div className="mt-1">
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="input-field"
                  placeholder="Enter your email"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="password"
                className="block text-sm font-medium theme-text-secondary"
              >
                {/* Password */}
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="input-field pr-10"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 theme-text-quaternary" />
                  ) : (
                    <Eye className="h-5 w-5 theme-text-quaternary" />
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-[#2AB7A6] focus:ring-[#2AB7A6] theme-border rounded theme-bg-quaternary"
                />
                <label
                  htmlFor="remember-me"
                  className="ml-2 block text-sm theme-text-secondary"
                >
                  Remember me
                </label>
              </div>

              <div className="text-sm">
                <Link
                  to="/forgot-password"
                  className="font-medium text-[#2AB7A6] hover:text-[#25A395]"
                >
                  Forgot your password?
                </Link>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-[#2AB7A6] hover:bg-[#25A395] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2AB7A6] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 group relative"
                title="Sign in"
              >
                {isLoading ? (
                  'Signing in...'
                ) : (
                  <>
                    {/* <Maximize2 className="h-4 w-4 mr-2" /> */}
                    Sign In
                  </>
                )}
              </button>
              
              {/* Fullscreen Info Tooltip */}
              {/* <div className="mt-2 text-center">
                <p className="text-xs text-gray-400">
                  Sign in to access Live Trading Studio in fullscreen mode
                </p>
              </div> */}
            </div>
          </form>



                     <div className="mt-6">
             <div className="auth-divider">
               <div className="auth-divider-line" />
               <div className="auth-divider-text">
                 <span className="px-2">
                   Or continue with
                 </span>
               </div>
             </div>

            <div className="mt-6 grid grid-cols-2 gap-2">
                             <button 
                 onClick={() => handleSocialSignIn('Google', signInWithGoogle)}
                 disabled={socialLoading !== null}
                 className="social-button"
               >
                <svg className="h-4 w-4" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                <span className="ml-1 text-xs">
                  {socialLoading === 'Google' ? '...' : 'Google'}
                </span>
              </button>
{/* 
              <button 
                onClick={() => handleSocialSignIn('Twitter', signInWithTwitter)}
                disabled={socialLoading !== null}
                className="w-full inline-flex justify-center py-2 px-2 border border-gray-600 rounded-lg shadow-sm bg-gray-700 text-sm font-medium text-gray-300 hover:bg-gray-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg
                  className="h-4 w-4"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" />
                </svg>
                <span className="ml-1 text-xs">
                  {socialLoading === 'Twitter' ? '...' : 'Twitter'}
                </span>
              </button>

              <button 
                onClick={() => handleSocialSignIn('Instagram', signInWithInstagram)}
                disabled={socialLoading !== null}
                className="w-full inline-flex justify-center py-2 px-2 border border-gray-600 rounded-lg shadow-sm bg-gray-700 text-sm font-medium text-gray-300 hover:bg-gray-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
                <span className="ml-1 text-xs">
                  {socialLoading === 'Instagram' ? '...' : 'Instagram'}
                </span>
              </button> */}

                             <button 
                 onClick={() => handleSocialSignIn('Facebook', signInWithFacebook)}
                 disabled={socialLoading !== null}
                 className="social-button"
               >
                <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                <span className="ml-1 text-xs">
                  {socialLoading === 'Facebook' ? '...' : 'Facebook'}
                </span>
              </button>
            </div>
                         <p className="auth-subtitle mt-6 text-center">
               Don't have an account?{' '}
               <Link
                 to="/register"
                 className="font-medium text-[#2AB7A6] hover:text-[#25A395]"
               >
                 Sign Up
               </Link>
             </p>
          </div>
        </div>
      </div>

      {/* Pending Approval Popup */}
      {showPendingPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="theme-bg-secondary rounded-lg p-6 max-w-md w-full theme-border">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-yellow-500/20 p-3 rounded-full">
                <Clock className="h-8 w-8 text-yellow-500" />
              </div>
            </div>
            <h3 className="text-lg font-semibold theme-text-primary text-center mb-3">
              Account Under Review
            </h3>
            <p className="theme-text-secondary text-center mb-6 leading-relaxed">
              Your request for approval has been sent and is now under review. 
              We usually approve users with their documents verified within 24 hours.
            </p>
            <div className="flex justify-center">
              <button
                onClick={() => {
                  setShowPendingPopup(false);
                  setEmail('');
                  setPassword('');
                }}
                className="px-6 py-2 bg-[#2AB7A6] text-white rounded-lg hover:bg-[#25A395] transition-colors duration-200"
              >
                Return to Login
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rejected Account Popup */}
      {showRejectedPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="theme-bg-secondary rounded-lg p-6 max-w-md w-full theme-border">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-red-500/20 p-3 rounded-full">
                <AlertCircle className="h-8 w-8 text-red-500" />
              </div>
            </div>
            <h3 className="text-lg font-semibold theme-text-primary text-center mb-3">
              Account Rejected
            </h3>
            <p className="theme-text-secondary text-center mb-6 leading-relaxed">
              Your account has been rejected. Please contact support for more information 
              or to appeal this decision.
            </p>
            <div className="flex justify-center">
              <button
                onClick={() => {
                  setShowRejectedPopup(false);
                  setEmail('');
                  setPassword('');
                }}
                className="px-6 py-2 bg-[#2AB7A6] text-white rounded-lg hover:bg-[#25A395] transition-colors duration-200"
              >
                Return to Login
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Suspended User Popup */}
      <SuspendedUserPopup
        isOpen={showSuspendedPopup}
        onClose={() => {
          setShowSuspendedPopup(false);
          setEmail('');
          setPassword('');
        }}
        onResubmit={() => {
          setShowSuspendedPopup(false);
          // Could redirect to KYC form or contact support
          toast('Please contact support to resolve your account suspension.');
        }}
        reason={suspendedUserReason}
      />
    </div>
  );
};

export default Login;
